import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import { useMutation } from '@apollo/react-hooks';
import type { ExecutionResult } from 'react-apollo';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';

import {
	ADD_INLINE_COMMENT_EXPERIENCE,
	ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
	ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	ExperienceTrackerContext,
	CREATE_INLINE_COMMENT_EXPERIENCE,
	createInlineCommentCompoundExperience,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker';
import { CommentEditor, clearCommentDraft } from '@confluence/comment';
import { useSessionData } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql';
import { useCommentsContentActions } from '@confluence/comment-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { CreateInlineCommentMutation } from '@confluence/inline-comments-queries';
import type {
	CreateInlineCommentLocation,
	ContentRepresentation,
	CreateInlineCommentMutationData,
	CreateInlineCommentMutationVariables,
} from '@confluence/inline-comments-queries';
import { usePageInfo } from '@confluence/page-info';
import { CommentAuthor, InlineCommentFramework } from '@confluence/inline-comments-common';
import {
	parseError,
	getTranslatedError,
	isOutOfDateError,
	isHighlightError,
	isUnexpectedError,
	isAnnotationError,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils';
import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store';
import { i18n as inlineCommentCommonI18n } from '@confluence/inline-comments-common/entry-points/i18n';
import { constructStepForGql } from '@confluence/comments-util';
import { usePageClass } from '@confluence/comments-hooks';
import {
	useInlineCommentsActions,
	useCommentSidebarOffset,
	useShouldCommentSidebarBeSticky,
} from '@confluence/inline-comments-hooks';
import { END } from '@confluence/navdex';
import {
	useCommentsDataActions,
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data';
import { fg } from '@confluence/feature-gating';
import { useWindowSize } from '@confluence/dom-helpers/entry-points/useWindowSize';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { useContentRefetchDispatch } from '@confluence/content-body';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';

import { CommentSidebar } from './CommentSidebar';
import type { SelectionOptions } from './renderer/SelectionComponent';

export const CREATE_TIMEOUT = 15000;

const i18n = defineMessages({
	unableToApplyTitle: {
		id: 'annotation-provider-inline-comments.create.error.unable.to.apply.title',
		defaultMessage: 'Something went wrong',
		description: 'The title of the flag shown to a user something went wrong',
	},
	unableToApplyDescription: {
		id: 'annotation-provider-inline-comments.create.error.unable.to.apply.description',
		defaultMessage: 'We failed to display your comment. To view it, refresh now.',
		description:
			'A message to inform the user we failed to show their newly created comment could not be shown (Comment is a noun)',
	},
	refreshContentLink: {
		id: 'annotation-provider-inline-comments.refresh.content.link',
		defaultMessage: 'Refresh content',
		description: 'A link for the user to click to refresh the content',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `${token('space.150')} ${token('space.200')}`,
});

type CreateCommentProps = {
	pageId: string;
	annotationElement?: HTMLElement | null;
	selectionOptions?: SelectionOptions;
	onCreate: (annotationId: string) => void;
	onClose?: () => void;
	isEditor?: boolean;
	isOpeningMediaCommentFromToolbar?: boolean;
};

const getNodeType = (element: HTMLElement, annotationId: string) => {
	const nodeWithAnnotationWrapper = element.querySelector(`[id="${annotationId}"]`);
	if (nodeWithAnnotationWrapper) {
		const firsChildren = nodeWithAnnotationWrapper.firstChild;
		if (firsChildren?.nodeType === Node.TEXT_NODE) {
			return 'text';
		}

		const targetNode = nodeWithAnnotationWrapper.querySelector('[data-node-type]');
		if (targetNode) {
			return (targetNode as HTMLElement).dataset.nodeType;
		}
	}

	return 'unknown';
};

// Exporting for testing cases
export const handleDraftAnnotationDeletion =
	(setWasDraftAnnotationRemoved: (isRemoved: boolean) => void) =>
	(mutationList: MutationRecord[], observer: MutationObserver) => {
		for (const mutation of mutationList) {
			if (mutation.type === 'childList') {
				if (mutation.removedNodes.length > 0) {
					mutation.removedNodes.forEach((node) => {
						const nodeClassList = Array.from((node as HTMLElement).classList ?? []);

						// COMMENTS-4308 - If any part of the draft annotation was removed, check if the entirity of the draft
						// 				   was removed, if so, we need to disable the editor buttons as the comment will fail
						if (nodeClassList.includes('ak-editor-annotation-draft')) {
							if (document.querySelector('.ak-editor-annotation-draft') === null) {
								// Tell the editor that the draft annotation was removed
								setWasDraftAnnotationRemoved(true);

								// Disconnect the observer since we no longer need it
								observer.disconnect();
							}
						}
					});
				}
			}
		}
	};

const CreateCommentComponent: FC<CreateCommentProps & WithFlagsProps> = ({
	pageId,
	annotationElement,
	selectionOptions,
	onClose,
	onCreate,
	isEditor,
	isOpeningMediaCommentFromToolbar,
	flags,
}) => {
	const started = useRef(false);
	const pageClass = usePageClass();
	if (fg('confluence_comments_experiences_thresholds')) {
		if (started.current === false) {
			createInlineCommentCompoundExperience.draftToPublishExperience.start();

			started.current = true;
		}
	}

	const initialisedSelection = useRef(false);
	if (
		selectionOptions &&
		selectionOptions?.createdFrom === 'EDITOR' &&
		initialisedSelection.current === false
	) {
		initialisedSelection.current = true;
		if (selectionOptions.inlineNodeTypes) {
			createInlineCommentCompoundExperience.addCommonAttributes({
				inlineNodeTypes: selectionOptions.inlineNodeTypes,
			});
		}
	}

	const { formatMessage } = useIntl();
	const pageMode = useGetPageMode();
	const lastContentFetchTime = useRef(1);
	const requestTimeoutHandle = useRef<ReturnType<typeof setTimeout>>();
	const sidebarEl = useRef<HTMLDivElement | null>(null);
	const draftAnnotationMutationObserverRef = useRef<MutationObserver>();
	const commentContainerElementRef = useRef<HTMLDivElement | null>(null);
	const { documentUpdated, publishedDocumentVersion } = useDocumentUpdateStatus();
	const { setRefetchContent } = useContentRefetchDispatch();

	const [shouldRefetch, setShouldRefetch] = useState(false);
	const [wasDraftAnnotationRemoved, setWasDraftAnnotationRemoved] = useState(false);

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { onChange, resetContentChanged } = useCommentsContentActions();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addUnresolvedInlineComment } = useInlineCommentsActions();
	const { userId, cloudId } = useSessionData();
	const { addNewCommentThreads } = useCommentsDataActions();
	const { width: windowWidth } = useWindowSize();

	const { isSticky } = useShouldCommentSidebarBeSticky({
		annotationElement,
		isEditor: !!isEditor,
	});

	const sidebarOffset = useCommentSidebarOffset({
		isEditor: Boolean(isEditor),
		annotationElement,
		windowWidth,
		isSticky,
	});

	const [createCommentFn] = useMutation<
		CreateInlineCommentMutationData,
		CreateInlineCommentMutationVariables
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
	>(CreateInlineCommentMutation);

	const { pageInfo, loading, error, refetch } = usePageInfo({
		fetchPolicy: fg('confluence-comment-create-pageInfo-use-cache-first')
			? 'cache-first'
			: 'network-only',
		onError: (err) => {
			experienceTracker.stopOnError({
				name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				error: err,
			});
		},
	});

	useEffect(() => {
		// should only refresh on the Content Out of date `Refresh` CTA is clicked
		if (documentUpdated && shouldRefetch && refetch) {
			void refetch();
			setShouldRefetch(false);
			onClose && onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentUpdated, shouldRefetch]);

	useEffect(() => {
		const metadata = document.querySelector('meta[name=confluence-request-time]');

		if (metadata) {
			const fetchTime = Number(metadata.getAttribute('content'));
			lastContentFetchTime.current = isNaN(fetchTime) ? 1 : fetchTime;
		}

		if (pageMode !== PageMode.VIEW && fg('confluence_frontend_handle_draft_annotation')) {
			const editorTextArea = document.getElementById('ak-editor-textarea');
			draftAnnotationMutationObserverRef.current = new MutationObserver(
				handleDraftAnnotationDeletion(setWasDraftAnnotationRemoved),
			);

			// We need to watch the whole editorTextArea because highlights could
			if (editorTextArea) {
				draftAnnotationMutationObserverRef.current.observe(editorTextArea, {
					childList: true,
					subtree: true,
				});
			}
		}

		return () => {
			if (
				pageMode !== PageMode.VIEW &&
				draftAnnotationMutationObserverRef.current &&
				fg('confluence_frontend_handle_draft_annotation')
			) {
				draftAnnotationMutationObserverRef.current.disconnect();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const saveAttemptedTracker = useRef<{
		totalSaves: number;
		failedSaves: number;
		lastPublishFailure?: Error;
		lastMutationSucceeded: boolean;
	}>({
		lastMutationSucceeded: false,
		totalSaves: 0,
		failedSaves: 0,
	});
	useEffect(() => {
		return () => {
			if (fg('confluence_comments_experiences_thresholds')) {
				const savesWithoutError =
					// hit, the experience will already have completed in the `handleSaveComment`.
					saveAttemptedTracker.current.totalSaves - saveAttemptedTracker.current.failedSaves;

				if (saveAttemptedTracker.current.failedSaves > 0) {
					createInlineCommentCompoundExperience.addCommonAttributes({
						// eslint-disable-next-line react-hooks/exhaustive-deps
						failedSaves: saveAttemptedTracker.current.failedSaves,
					});
				}

				if (savesWithoutError > 1) {
					// At the point this is dismounting -- we don't know for certain if the user intended for this to be discarded or not
					// so we hard fail the full experience (it's possible the dismount is because the second save has worked and
					// the UI is about to load the new comment)
					createInlineCommentCompoundExperience.addCommonAttributes({
						savesWithoutError,
					});
					createInlineCommentCompoundExperience.draftToPublishExperience.fail(
						new Error('User clicked save, but UI did not update following successful save'),
					);

					return;
				}

				if (saveAttemptedTracker.current.lastPublishFailure) {
					if (saveAttemptedTracker.current.lastMutationSucceeded) {
						// in the case there was a publish failure, but the last mutation succeeded -- we assume the dismount is
						// because the ui is about to load the new comment - in this case we only mark the experience as a soft
						// failure (we'll capture more details and fail in the final experience step)
						createInlineCommentCompoundExperience.draftToPublishExperience.softFail(
							// eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentionally using a ref value due to the indirect flow of comment updates
							saveAttemptedTracker.current.lastPublishFailure,
						);

						return;
					} else {
						// In this scenario -- the user attempted to save at least once, got a network error, and gave up on
						// trying to create the comment
						createInlineCommentCompoundExperience.draftToPublishExperience.fail(
							// eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentionally using a ref value due to the indirect flow of comment updates
							saveAttemptedTracker.current.lastPublishFailure,
						);

						return;
					}
				}

				if (saveAttemptedTracker.current.lastMutationSucceeded) {
					createInlineCommentCompoundExperience.draftToPublishExperience.complete();

					return;
				}

				// in this case the user did not attempt to save the comment, and so we assume this was
				// an intentional discard of the comment
				createInlineCommentCompoundExperience.draftToPublishExperience.abort({
					abortReason: 'Create Comment UI dismissed',
				});
			} else {
				// Note: in the case of a successful comment creation, while this code path will be
				// hit, the experience will already have completed in the `handleSaveComment`.
				createInlineCommentCompoundExperience.draftToPublishExperience.dismissed();
			}
		};
	}, []);

	const saveAttemptedCounts = useRef<{ total: number; failedSaves: number }>({
		total: 0,
		failedSaves: 0,
	});

	const handleSaveCommentWithTimeout = (timeoutInMs: number, saveMutation: Promise<void>) => {
		const timeout = new Promise((_resolve, reject) => {
			requestTimeoutHandle.current = setTimeout(() => {
				experienceTracker.stopOnError({
					name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					error: new Error(`Request took longer than ${timeoutInMs}ms`),
				});

				return reject({ error: inlineCommentCommonI18n.createTimeout });
			}, timeoutInMs);
		});

		return Promise.race([timeout, saveMutation]).finally(() => {
			if (requestTimeoutHandle.current) {
				clearTimeout(requestTimeoutHandle.current);
			}
		});
	};

	const handleSaveComment = (adf: JSONDocNode, onSuccess: () => void) => {
		if (fg('confluence_comments_experiences_thresholds')) {
			saveAttemptedTracker.current.totalSaves = saveAttemptedTracker.current.totalSaves + 1;
			saveAttemptedTracker.current.lastMutationSucceeded = false;
		}
		saveAttemptedCounts.current.total = saveAttemptedCounts.current.total + 1;

		if (!selectionOptions) {
			// We likely will never get here, but just in case
			return Promise.reject({ error: inlineCommentCommonI18n.unableToGenerateStep });
		}

		experienceTracker.start({
			name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
			attributes: {
				timeout: CREATE_TIMEOUT,
			},
		});

		const { numMatches, matchIndex, originalSelection, createdFrom, step, targetNodeType } =
			selectionOptions;

		const variables = {
			input: {
				containerId: pageId,
				commentBody: {
					value: JSON.stringify(adf),
					representationFormat: 'ATLAS_DOC_FORMAT' as ContentRepresentation,
				},
				numMatches,
				matchIndex,
				originalSelection,
				createdFrom,
				lastFetchTimeMillis: lastContentFetchTime.current,
				publishedVersion: publishedDocumentVersion,
				...(!!step && { step: constructStepForGql(step) }),
			},
			pageId,
		};

		// Note -- this mixes data saving logic and UI application logic.
		// And when the UI logic fails, the result is that the user is left with
		// the ui looking like the comment failed to save.
		// This should be refactored to separate the two concerns, and on a UI application
		// failure, we should encourage users to refresh (and mark the experience as failed).
		let succeededPublish = false;

		const onCreateSuccess = (retVal: ExecutionResult<CreateInlineCommentMutationData>) => {
			if (fg('confluence_comments_experiences_thresholds')) {
				saveAttemptedTracker.current.lastMutationSucceeded = true;
			}
			const { data } = retVal;
			if (requestTimeoutHandle.current) {
				clearTimeout(requestTimeoutHandle.current);
			}

			succeededPublish = true;
			createInlineCommentCompoundExperience.draftToPublishExperience.publishSucceeded();
			const annotationId = (data?.createInlineComment?.location as CreateInlineCommentLocation)
				?.inlineMarkerRef;
			const commentId = data?.createInlineComment?.id;

			if (!annotationId || !commentId) {
				throw new Error(
					`Comment was not saved correctly - either annotationId '${annotationId}' or commentId '${commentId}' were undefined`,
				);
			}

			// Reset the content changing flag
			resetContentChanged();

			// Grab the marker ref id returned and update the editor/renderer
			try {
				onCreate(annotationId);
			} catch (e) {
				// We generate errors in renderer, but this will also handle the editor case
				throw new Error(`Unable to apply annotation to document - ${e.message}`);
			}

			// Update the unresolved inline comments count
			addUnresolvedInlineComment(annotationId, pageMode);

			const newComment: CommentData = {
				...data?.createInlineComment,
				isUnread: false,
				isOpen: true,
				type: CommentType.INLINE,
				wasRemovedByAnotherUser: false,
				replies: [] as ReplyData[],
			};

			addNewCommentThreads({
				general: {},
				inline: {
					[annotationId]: newComment,
				},
			});

			// Create inline comment track event
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'created',
					actionSubject: 'comment',
					actionSubjectId: commentId,
					objectType: 'page',
					objectId: pageId,
					source: isEditor ? 'editPageScreen' : 'viewPageScreen',
					attributes: {
						source: AnalyticsSource.STANDALONE,
						commentType: CommentType.INLINE,
						parentCommentId: null,
						isReply: false,
						pageMode,
						isLivePage: createdFrom === 'LIVE',
						navdexPointType: END,
						inlineNodeTypes: selectionOptions.inlineNodeTypes,
						...(annotationElement &&
							!isEditor && {
								nodeType: getNodeType(annotationElement, annotationId),
							}),
						...(!isEditor && { targetNodeType }),
					},
				},
			}).fire();

			// Reset the editor
			onSuccess();

			if (!fg('confluence_comments_experiences_thresholds')) {
				// We expect there to be 1 save attempt more than failed saves
				// If this is not the case, we are hitting this code path for a second time which indicates
				// the user has clicked the "Save" button, but nothing has happened to the UI
				if (saveAttemptedCounts.current.total - 1 > saveAttemptedCounts.current.failedSaves) {
					const softFailError = new Error(
						'User clicked save, but UI did not update following successful save',
					);
					createInlineCommentCompoundExperience.draftToPublishExperience.debug({
						error: softFailError,
						createAnalyticsEvent,
						extraAttributes: {
							savesWithoutError:
								saveAttemptedCounts.current.total - saveAttemptedCounts.current.failedSaves,
						},
					});
					createInlineCommentCompoundExperience.draftToPublishExperience.softFail(softFailError);
				} else {
					createInlineCommentCompoundExperience.draftToPublishExperience.complete();
				}
			}

			if (fg('confluence_comments_select_comment_experience')) {
				const linkedCompoundTaskId = experienceTracker.getExperienceState(
					CREATE_INLINE_COMMENT_EXPERIENCE,
				)?.taskId;
				selectInlineCommentCompoundExperience.selectAnnotation.start({
					pageClass,
					commentType: 'inline',
					entryPoint: 'create-comment',
					annotationId,
					linkedCompoundTaskId,
				});
			} else {
				createInlineCommentCompoundExperience.attachCommentExperience.start();
			}
		};

		const onCreateFailure = (err: Error) => {
			if (fg('confluence_comments_experiences_thresholds')) {
				saveAttemptedTracker.current.failedSaves = saveAttemptedTracker.current.failedSaves + 1;
			}
			saveAttemptedCounts.current.failedSaves = saveAttemptedCounts.current.failedSaves + 1;
			createInlineCommentCompoundExperience.draftToPublishExperience.debug({
				error: err,
				createAnalyticsEvent,
				extraAttributes: {
					pointOfFailure: !succeededPublish ? 'running-mutation' : 'applying-comment-to-ui',
				},
			});

			if (!succeededPublish) {
				if (fg('confluence_comments_experiences_thresholds')) {
					saveAttemptedTracker.current.lastPublishFailure = err;
				} else {
					createInlineCommentCompoundExperience.draftToPublishExperience.publishFailed(err);
				}
			}
			let contentOutOfDate = false;
			// Get a truncated graphql error message
			const { errorId, message } = parseError(err);
			const translatedError = getTranslatedError(message);

			// COMMENTS-1879 - The backend will fall back to the old method of trying to make string matches if the content
			// is out of date. If the BE fails to apply a comment when using the old fallback method we should log it
			if (isHighlightError(translatedError)) {
				// Send the analytics event
				const analyticsObject = {
					source: 'viewPage',
					objectType: 'page',
					objectId: pageId,
					action: 'invalid',
					actionSubject: 'highlight',
					actionSubjectId: 'inlineComment',
					attributes: {
						errorMessage: message,
						editor: 'FABRIC',
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						errorId,
						highlightOrigin: 'userHighlight', // TODO: Do we need to programmatically track this?
					},
				};

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: analyticsObject,
				}).fire();

				contentOutOfDate = true;
			}

			// COMMENTS-1879 - Only log unexpected errors or annotation application error as failures
			if (isUnexpectedError(translatedError)) {
				experienceTracker.stopOnError({
					name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					error: err,
				});
			} else {
				markErrorAsHandled(err);
			}

			// Use the existing value if it's already set or is an out of date error
			contentOutOfDate = contentOutOfDate || isOutOfDateError(translatedError);

			// In the case of an annotation error, we should show a flag to the user to let them know
			// their comment has been saved and they need to refresh the content (view mode only)
			if (isAnnotationError(translatedError) && fg('confluence_frontend_handle_annotation_error')) {
				const CREATE_ERROR_FLAG_ID = `create-error-${Date.now()}`;
				void flags.showWarningFlag({
					id: CREATE_ERROR_FLAG_ID,
					title: <FormattedMessage {...i18n.unableToApplyTitle} />,
					description: (
						<FormattedMessage
							{...(translatedError === inlineCommentCommonI18n.unableToApplyAnnotation
								? i18n.unableToApplyDescription
								: translatedError)}
						/>
					),
					actions:
						// Only show the refetch content action on view mode as it's not applicable in editor/live
						pageMode === PageMode.VIEW
							? [
									{
										content: formatMessage(i18n.refreshContentLink),
										onClick: () => {
											setRefetchContent(true);
											handleOnClose();
											void flags.hideFlag(CREATE_ERROR_FLAG_ID);
										},
									},
								]
							: // TODO: Add actions for editor/live if needed
								[],
				});

				// Log the occurance through analytics so we can still track it
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						objectType: 'page',
						objectId: pageId,
						action: 'failed',
						actionSubject: 'applyAnnotation',
						actionSubjectId: 'inlineComment',
						attributes: {
							errorMessage: message,
							editor: 'FABRIC',
							framework: InlineCommentFramework.ANNOTATION_PROVIDER,
							isLivePage: pageMode === PageMode.LIVE,
							tenantId: cloudId,
							userId,
							pageId,
						},
					},
				}).fire();

				if (fg('confluence_comments_experiences_thresholds')) {
					createInlineCommentCompoundExperience.draftToPublishExperience.abort({
						abortReason: 'Unable to apply annotation to document',
					});
				}

				onClose && onClose();

				return;
			}

			// CommentEditor expects a translated error message otherwise it fails
			// TODO: CommentEditor should likely just receive the translated message
			// rather than expect the object needed for FormattedMessage
			return Promise.reject({
				error: contentOutOfDate ? inlineCommentCommonI18n.contentOutOfDate : translatedError,
				contentOutOfDate,
			});
		};

		if (fg('confluence_frontend_add_inline_comment_timeout')) {
			return handleSaveCommentWithTimeout(
				CREATE_TIMEOUT,
				createCommentFn({ variables }).then(onCreateSuccess).catch(onCreateFailure),
			);
		} else {
			return createCommentFn({ variables }).then(onCreateSuccess).catch(onCreateFailure);
		}
	};

	const handleOnClose = useCallback(
		(hasContentChanged?: boolean) => {
			const reason = hasContentChanged
				? 'comment discarded by user'
				: 'empty comment discarded by user';

			experienceTracker.abort({
				name: ADD_INLINE_COMMENT_EXPERIENCE,
				reason,
			});

			// Clearing reply draft is handled in the comment editor
			clearCommentDraft(isEditor ? 'edit-inline' : 'inline', 'create');

			onClose && onClose();
		},
		[experienceTracker, isEditor, onClose],
	);

	const onEditorLoad = () => {
		experienceTracker.succeed({
			name: ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
		});
		if (fg('confluence_comments_experiences_thresholds')) {
			const draftAnnotationElement =
				document.querySelector('[data-annotation-draft-mark="true"]') ||
				document.querySelector('.ak-editor-annotation-draft');

			if (!draftAnnotationElement) {
				createInlineCommentCompoundExperience.initExperience.fail(
					new Error('No annotation element'),
				);

				return;
			}

			setTimeout(() => {
				// There are multiple useEffects involved in positioning the comment UI.
				// This works around this by only marking the experience as complete after
				// a short timeout + animation frame.
				requestAnimationFrame(() => {
					// In the Editor and Renderer, the comment UI is positioned relative to slightly different
					// elements.

					if (!draftAnnotationElement) {
						createInlineCommentCompoundExperience.initExperience.abort({
							abortReason: 'Draft annotation removed from document',
						});

						return;
					} else if (!sidebarEl.current) {
						createInlineCommentCompoundExperience.initExperience.fail(
							new Error('Missing create comment ui'),
						);
					} else {
						const commentUIVerticalDistanceFromAnnotation = Math.abs(
							draftAnnotationElement.getBoundingClientRect().top -
								sidebarEl.current.getBoundingClientRect?.().top,
						);

						// The 40 pixel allowance has been arbitrarily chosen to allow for some wiggle room
						if (commentUIVerticalDistanceFromAnnotation > 40) {
							const isVisible = sidebarEl.current.checkVisibility();
							if (isVisible) {
								// if it's fully visible, but misaligned, we don't treat this as a failure
								createInlineCommentCompoundExperience.initExperience.debug({
									error: new Error('View Created Comment UI too far from annotation'),
									createAnalyticsEvent,
									extraAttributes: { misalignedBy: commentUIVerticalDistanceFromAnnotation },
								});
							} else {
								createInlineCommentCompoundExperience.addCommonAttributes({
									misalignedBy: commentUIVerticalDistanceFromAnnotation,
								});
								createInlineCommentCompoundExperience.initExperience.softFail(
									new Error('Draft Comment UI is setup too far from annotation'),
								);
							}
						} else {
							createInlineCommentCompoundExperience.initExperience.complete();
						}
					}
				});
			}, 1000);
		} else {
			setTimeout(() => {
				// There are multiple useEffects involved in positioning the comment UI.
				// This works around this by only marking the experience as complete after
				// a short timeout + animation frame.
				requestAnimationFrame(() => {
					// In the Editor and Renderer, the comment UI is positioned relative to slightly different
					// elements.
					const draftAnnotationElement =
						document.querySelector('[data-annotation-draft-mark="true"]') ||
						document.querySelector('.ak-editor-annotation-draft');

					if (!draftAnnotationElement) {
						createInlineCommentCompoundExperience.initExperience.fail(
							new Error('No annotation element'),
						);
					} else if (!sidebarEl.current) {
						createInlineCommentCompoundExperience.initExperience.fail(
							new Error('Missing create comment ui'),
						);
					} else {
						const commentUIVerticalDistanceFromAnnotation = Math.abs(
							draftAnnotationElement.getBoundingClientRect().top -
								sidebarEl.current.getBoundingClientRect?.().top,
						);

						// The 10 pixel allowance has been arbitrarily chosen to allow for some wiggle room
						if (commentUIVerticalDistanceFromAnnotation > 10) {
							createInlineCommentCompoundExperience.initExperience.softFail(
								new Error('Draft Comment UI is setup too far from annotation'),
							);
							createInlineCommentCompoundExperience.draftToPublishExperience.start();
						} else {
							createInlineCommentCompoundExperience.initExperience.complete();
							createInlineCommentCompoundExperience.draftToPublishExperience.start();
						}
					}
				});
			}, 1000);
		}
	};

	if (loading || error) {
		return null;
	}

	const spaceId = pageInfo?.space?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	// The user can upload media only if they have update permissions for the page
	const hasMediaUploadPermissions = operations.some(
		(op) => op?.operation === 'update' && op?.targetType === pageType,
	);

	return (
		<CommentSidebar
			pageId={pageId}
			onClose={handleOnClose}
			isEditor={isEditor}
			annotationElement={annotationElement}
			sidebarOffset={sidebarOffset}
			scrollIntoView
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			sidebarEl={sidebarEl}
			isSticky={isSticky}
		>
			<Container ref={commentContainerElementRef}>
				<CommentAuthor commentMode="create" userId={userId} />
				<CommentEditor
					// @ts-ignore FIXME: `pageId` can be `undefined` here, and needs proper handling
					pageId={pageId}
					pageType={pageType}
					spaceId={spaceId}
					appearance="chromeless"
					EditorComponent={ChromelessEditor}
					onSaveComment={handleSaveComment}
					onContentChange={onChange}
					commentMode="create"
					commentType={isEditor ? 'edit-inline' : 'inline'}
					onEditorReady={onEditorLoad}
					hideWatchCheckbox
					pageMode={isEditor ? 'edit' : 'view'}
					shouldWarnOnInternalNavigation
					useNewWarningModal
					setShouldRefetch={() => setShouldRefetch(true)}
					hasMediaUploadPermissions={hasMediaUploadPermissions}
					commentContainerElementRef={commentContainerElementRef}
					wasDraftAnnotationRemoved={wasDraftAnnotationRemoved}
					isStandaloneComment
				/>
			</Container>
		</CommentSidebar>
	);
};

export const CreateComment = withFlags(CreateCommentComponent);
